import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent,
  Button,
  useTheme,
} from '@mui/material'
import { Link } from 'react-router-dom'
import siteConfig from '../config/siteConfig'
import { images } from '../config/images'

const Projects: React.FC = () => {
  const theme = useTheme()

  const projects = [
    {
      id: 1,
      title: 'Union 1',
      category: 'Current',
      status: 'In Development',
      description: 'Revolutionary hybrid rocket motor with 30% improved efficiency over traditional designs. Features advanced fuel injection system and optimized combustion chamber.',
      image: '🚀',
      achievements: ['2 Patents Filed', 'NASA USRP Collaboration', 'Best Innovation Award 2024'],
      details: 'Our flagship project developing next-generation hybrid propulsion technology for competition and commercial applications.',
    },
    {
      id: 2,
      title: 'Union 2',
      category: 'Current',
      status: 'Testing Phase',
      description: 'High-performance competition rocket designed for the 2025 International Rocketry Championship in Brazil. Features advanced recovery system and telemetry.',
      image: '🚀',
      achievements: ['Qualification Passed', 'Safety Certification', '15km Altitude Target'],
      details: 'Competition rocket built to compete at the highest international level with advanced features and reliable performance.',
    },
    {
      id: 3,
      title: 'Delos 1',
      category: 'Current',
      status: 'Prototype',
      description: 'AI-powered autonomous recovery system for rocket competitions. Uses computer vision and machine learning for precision landing.',
      image: '🚀',
      achievements: ['Prototype Working', 'Algorithm Validated', 'Patent Pending'],
      details: 'Cutting-edge recovery system that will revolutionize rocket landing accuracy in competitions.',
    },
    {
      id: 4,
      title: 'MCHP1',
      category: 'Completed',
      status: 'Competition Winner',
      description: 'Competition rocket that won 1st place at the 2023 Latin American Rocketry Championship. Featured innovative hybrid motor design.',
      image: '🚀',
      achievements: ['1st Place Winner', '12.5km Altitude', 'Perfect Recovery', 'Team Spirit Award'],
      details: 'Our most successful competition rocket to date, setting multiple records and winning prestigious awards.',
    },
  ]

  const pastAchievements = [
    {
      year: '2025',
      event: 'ENMICE 2025',
      location: 'Guadalajara, Mexico',
      details: '--- Details to be added ---',
      image: '🥇',
    },
    {
      year: '2024',
      event: 'BAJA Rocket Challenge',
      location: 'Tijuana, Mexico',
      details: '--- Details to be added ---',
      image: '🏆',
    },
    {
      year: '2023',
      event: 'ENMICE 2023-2024',
      location: 'Chihuahua, Mexico',
      details: '--- Details to be added ---',
      image: '🥈',
    },
    {
      year: '2023',
      event: 'LASC 2023',
      location: 'Tatuí, Brazil',
      details: '--- Details to be added ---',
      image: '🚀',
    },
    {
      year: '2022',
      event: 'ENMICE 2022',
      location: 'Guadalajara, Mexico',
      details: 'Our First Competition as Participants.',
      image: '🌟',
    },
    {
      year: '2021',
      event: 'ENMICE 2021',
      location: 'Tijuana, Mexico',
      details: 'Experienced our first competition as spectators.',
      image: '🎯',
    },
  ]

  const statistics = [
    { label: 'Competitions', value: '4', icon: '🏆' },
    { label: 'Cities Competed', value: '4', icon: '🌎' },
    { label: 'Failed Flights', value: '2', icon: '💔' },
    { label: 'Successful Flights', value: '5', icon: '🎯' },
  ]

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(rgba(255, 0, 0, 0.3), rgba(0, 0, 0, 0.5)), url(${images.backgrounds.projectsHero}) center/cover`,
          color: 'white',
          py: 12,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: '3rem', md: '4.5rem' },
              fontWeight: 800,
              mb: 3,
            }}
          >
            Projects & Competitions
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.2rem', md: '1.5rem' },
              maxWidth: '800px',
              mx: 'auto',
              opacity: 0.9,
              lineHeight: 1.6,
            }}
          >
            From groundbreaking research to international rocketry competitions - explore our journey of innovation
          </Typography>
        </Container>
      </Box>

      {/* Statistics - from Events */}
      {siteConfig.projects.statistics && (
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Grid container spacing={4}>
            {statistics.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 4,
                    background: 'linear-gradient(135deg, #FF0000 0%, #FF4444 100%)',
                    color: 'white',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 15px 35px rgba(255,0,0,0.3)',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '3rem', mb: 2 }}>
                    {stat.icon}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 800, mb: 1 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {stat.label}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* Project Categories */}
      {siteConfig.projects.projectCards && (
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontSize: { xs: '2.5rem', md: '3rem' },
              fontWeight: 700,
              mb: 6,
            }}
          >
            Our Projects
          </Typography>

          {/* Projects Grid */}
          <Grid container spacing={4}>
            {projects.map((project) => (
              <Grid item xs={12} md={6} lg={4} key={project.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(255,0,0,0.2)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: 200,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #FF6666 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '4rem',
                      position: 'relative',
                    }}
                  >
                    {project.image}
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontWeight: 700, mb: 2 }}
                    >
                      {project.title}
                    </Typography>
                    
                    {/*<Typography
                      variant="body2"
                      paragraph
                      sx={{ lineHeight: 1.6, mb: 3 }}
                    >
                      {project.description}
                    </Typography>*/}

                    {/* Achievements */}
                    {/*<Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Key Achievements:
                      </Typography>
                      {project.achievements.slice(0, 3).map((achievement, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          sx={{ fontSize: '0.85rem', mb: 0.5 }}
                        >
                          • {achievement}
                        </Typography>
                      ))}
                    </Box>*/}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* Past Achievements Timeline */}
      {siteConfig.projects.achievements && (
        <Container maxWidth="lg" sx={{ py: 10 }}>
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontSize: { xs: '2.5rem', md: '3rem' },
              fontWeight: 700,
              mb: 8,
            }}
          >
            Our Achievement Timeline
          </Typography>
          
          <Box sx={{ position: 'relative' }}>
            {pastAchievements.map((achievement, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  mb: 6,
                  alignItems: 'flex-start',
                  position: 'relative',
                }}
              >
                {/* Timeline Year */}
                <Box
                  sx={{
                    minWidth: 80,
                    textAlign: 'center',
                    mr: 4,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 1,
                    }}
                  >
                    {achievement.year}
                  </Typography>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '50%',
                      mx: 'auto',
                    }}
                  />
                </Box>
                
                {/* Achievement Card */}
                <Card sx={{ flex: 1, p: 3, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Typography sx={{ fontSize: '2rem' }}>
                      {achievement.image}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                        {achievement.event}
                      </Typography>
                      {/*<Typography variant="body2" color="text.secondary" paragraph>
                        {achievement.details}
                      </Typography>*/}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          📍 {achievement.location}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Box>
            ))}
          </Box>
        </Container>
      )}

      {/* Follow Our Journey Section - COMMENTED OUT */}
      {/* {false && (
        <Box sx={{ bgcolor: '#f8f9fa', py: 10 }}>
          <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                fontSize: { xs: '2.5rem', md: '3rem' },
                fontWeight: 700,
                mb: 4,
              }}
            >
              Follow Our Journey
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.3rem',
                maxWidth: '700px',
                mx: 'auto',
                mb: 6,
                lineHeight: 1.6,
              }}
            >
              Stay updated on our competition schedule, live results, and behind-the-scenes 
              content from our rocketry adventures around the world.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ px: 5, py: 2 }}
              >
                Competition Schedule
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{ px: 5, py: 2 }}
              >
                Live Updates
              </Button>
            </Box>
          </Container>
        </Box>
      )} */}

      {/* Call to Action */}
      {siteConfig.projects.callToAction && (
        <Box sx={{ bgcolor: '#f8f9fa', py: 10 }}>
          <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                fontSize: { xs: '2.5rem', md: '3rem' },
                fontWeight: 700,
                mb: 4,
              }}
            >
              Want to Collaborate?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.3rem',
                maxWidth: '700px',
                mx: 'auto',
                mb: 6,
                lineHeight: 1.6,
              }}
            >
              We're always looking for partners, sponsors, and collaborators to help us push 
              the boundaries of rocketry technology. Join us in shaping the future of space exploration.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                color="primary"
                size="large"
                sx={{ px: 5, py: 2 }}
              >
                Partner With Us
              </Button>
              {/* Research Papers Button - COMMENTED OUT */}
              {/* {siteConfig.projects.researchPapers && (
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ px: 5, py: 2 }}
                >
                  View Research Papers
                </Button>
              )} */}
            </Box>
          </Container>
        </Box>
      )}
    </Box>
  )
}

export default Projects
